import { clsx } from 'clsx';

/**
 * The Verydia Control mark.
 *
 * A concentric aperture: an outer ring (the control plane) around an inner core
 * (the licensed software), with a gap that closes only when the two are joined.
 * Drawn as inline SVG with `currentColor` plus one gradient stop so it inherits
 * whatever surface it sits on and needs no asset pipeline.
 */
export function VerydiaMark({ size = 22, className }: { size?: number; className?: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      className={className}
      role="img"
      aria-label="Verydia Control"
    >
      <defs>
        <linearGradient id="vc-mark-gradient" x1="3" y1="3" x2="21" y2="21">
          <stop offset="0%" stopColor="var(--vc-signal-300)" />
          <stop offset="100%" stopColor="var(--vc-iris-500)" />
        </linearGradient>
      </defs>
      {/* Outer aperture, deliberately open at the top-right. */}
      <path
        d="M20.5 8.2A9.5 9.5 0 1 0 21.5 12"
        stroke="url(#vc-mark-gradient)"
        strokeWidth="2.4"
        strokeLinecap="round"
      />
      <circle cx="12" cy="12" r="4.4" stroke="url(#vc-mark-gradient)" strokeWidth="2.4" />
      <circle cx="12" cy="12" r="1.5" fill="url(#vc-mark-gradient)" />
    </svg>
  );
}

/**
 * Which palette the wordmark draws from.
 *
 * `nav` is the sidebar's: light text sized for the dark navigation rail. `surface`
 * is the page's, and follows the theme. The distinction is load-bearing — the
 * nav palette is light in BOTH themes, because the rail is dark in both, so a
 * wordmark using it on a light page renders white on near-white and disappears.
 * That is exactly what it did on the sign-in page.
 */
export type BrandTone = 'nav' | 'surface';

const TONE_STRONG: Record<BrandTone, string> = {
  nav: 'text-[var(--vc-nav-text-strong)]',
  surface: 'text-[var(--vc-text)]',
};

const TONE_MUTED: Record<BrandTone, string> = {
  nav: 'text-[var(--vc-nav-text-muted)]',
  surface: 'text-[var(--vc-text-muted)]',
};

export function VerydiaWordmark({
  className,
  tone = 'nav',
}: {
  className?: string;
  tone?: BrandTone;
}) {
  return (
    <span className={clsx('flex min-w-0 items-center gap-2.5', className)}>
      <VerydiaMark size={22} className="shrink-0" />
      <span className="flex min-w-0 flex-col leading-none">
        <span
          className={clsx(
            'truncate text-[13px] font-semibold tracking-[-0.01em]',
            TONE_STRONG[tone],
          )}
        >
          Verydia Control
        </span>
        <span
          className={clsx(
            'mt-1 truncate text-[10px] font-medium tracking-[0.04em] uppercase',
            TONE_MUTED[tone],
          )}
        >
          by Parallax Motion
        </span>
      </span>
    </span>
  );
}
